import type { EventDetails, LinkedTT } from '@/api/Helpers'
import type { Session } from '@/api/Sessions'
import { sum } from '@/helpers/MiscellaneousHelpers'
import type { Moment } from 'moment-timezone'
import moment from 'moment-timezone'

export type MessageType = 'date' | 'session' | 'quantities'

export interface AnnotatedSession extends Session {
  annotations: MessageAction[]
}

export function uniqueAnnotations(sessions: AnnotatedSession[]): MessageAction[] {
  const result = new Set<MessageAction>()
  for (const session of sessions) {
    const annotations = session.annotations ?? []
    for (const annotation of annotations) {
      result.add(annotation)
    }
  }
  return Array.from(result)
}

export function annotateSession(
  session: Session,
  event: EventDetails,
  quantities?: TicketTypeQuantities,
): AnnotatedSession {
  return {
    ...session,
    annotations: getAnnotationsOrMessages(event, 'session', 'annotation', session.startMoment, quantities),
  }
}

export function getDateAnnotations(date: Moment, event: EventDetails, quantities?: TicketTypeQuantities) {
  return getAnnotationsOrMessages(event, 'date', 'annotation', date, quantities)
}

export function getMessages(event: EventDetails, type: MessageType, start?: Moment, quantities?: TicketTypeQuantities) {
  return getAnnotationsOrMessages(event, type, 'message', start, quantities)
}

function getAnnotationsOrMessages(
  event: EventDetails,
  type: MessageType,
  annotationOrMessage: 'annotation' | 'message',
  start?: Moment,
  quantities?: TicketTypeQuantities,
): MessageAction[] {
  const result: MessageAction[] = []

  const rules: MessageRule[] = event.messageRules ?? []
  for (const rule of rules) {
    const action = getMatchingAction(rule.actions, type, annotationOrMessage)
    if (action && ruleTriggered(event, rule, type, quantities, start)) {
      result.push(action)
    }
  }

  return result
}

function getMatchingAction(
  actions: MessageRule['actions'],
  type: MessageType,
  annotationOrMessage: 'annotation' | 'message',
) {
  if (type === 'quantities') {
    return actions.quantity_message
  } else if (type === 'session') {
    return annotationOrMessage === 'annotation' ? actions.session_annotation : actions.session_message
  } else {
    return annotationOrMessage === 'annotation' ? actions.date_annotation : actions.date_message
  }
}

function ruleTriggered(
  event: EventDetails,
  rule: MessageRule,
  type: MessageType,
  quantities?: TicketTypeQuantities,
  start?: Moment,
): boolean {
  return dateTimePassesTriggers(event, rule, type, start) && quantitiesPassesTriggers(event, rule, quantities)
}

function dateTimePassesTriggers(event: EventDetails, rule: MessageRule, type: MessageType, start?: Moment): boolean {
  const triggers = rule.triggers.date_or_session
  if (triggers === undefined || triggers.length === 0) {
    return true
  } else if (start === undefined) {
    return false
  } else {
    return triggers.some((trigger) => {
      return (
        // TODO session indices.
        matchesDateRange(trigger, start, event.venue.timezone) &&
        matchesDaysOfWeek(trigger, start) &&
        matchesTimeOfDay(trigger, start, type)
      )
    })
  }
}

function matchesDateRange(trigger: DateTimeTrigger, sessionStart: Moment, timezone: string): boolean {
  if (!trigger.date_range) {
    return true
  }

  const start = moment.tz(trigger.date_range.start, timezone)
  // Add 24 hours so that it is the end of that day.
  const end = moment.tz(trigger.date_range.end, timezone).add(24, 'hours')
  return sessionStart.isSameOrAfter(start) && sessionStart.isBefore(end)
}

function matchesDaysOfWeek(trigger: DateTimeTrigger, sessionStart: Moment): boolean {
  if (!trigger.weekdays) {
    return true
  }

  return trigger.weekdays.includes(sessionStart.day() as 0 | 1 | 2 | 3 | 4 | 5 | 6)
}

function matchesTimeOfDay({ time_of_day }: DateTimeTrigger, sessionStart: Moment, type: MessageType): boolean {
  if (!time_of_day || type === 'date') {
    return true
  }

  const session = minutesSinceStartOfDay(sessionStart.format('HH:mm'))
  const start = minutesSinceStartOfDay(time_of_day.start)
  const end = minutesSinceStartOfDay(time_of_day.end)
  return session >= start && session < end
}

function minutesSinceStartOfDay(time: string) {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

function quantitiesPassesTriggers(
  event: EventDetails,
  rule: MessageRule,
  quantities: TicketTypeQuantities = {},
): boolean {
  const triggers = rule.triggers.quantity
  if (triggers === undefined || triggers.length === 0) {
    return true
  } else {
    return triggers.every((trigger) => quantitiesMatch(event, trigger, quantities))
  }
}

function ticketTypeQuantitiesMatch(trigger: QuantityTrigger, quantities: TicketTypeQuantities, types: TicketType[]) {
  const matchingTypes = entitiesMatchingTrigger(types, trigger.ticket_type_ids, trigger.ticket_type_names)
  const selectedQuantities = sum(matchingTypes.map((type) => quantities[type.id]?.quantity ?? 0))
  return isWithinBounds(trigger, selectedQuantities)
}

function quantitiesMatch(event: EventDetails, trigger: QuantityTrigger, quantities: TicketTypeQuantities) {
  const types = getRelevantTicketTypes(event, trigger)
  return ticketTypeQuantitiesMatch(trigger, quantities, types)
}

function getRelevantTicketTypes(event: EventDetails, trigger: QuantityTrigger): LinkedTT[] {
  const matchingGroups = entitiesMatchingTrigger(
    event.ticketGroups,
    trigger.ticket_group_ids,
    trigger.ticket_group_names,
  )
  return matchingGroups.flatMap((t) => t.types)
}

function entitiesMatchingTrigger<T extends NamedApiEntity>(
  entities: T[],
  idFilter: string[] | undefined,
  nameFilter: string[] | undefined,
): T[] {
  if (!idFilter?.length && !nameFilter?.length) {
    return entities
  }

  return entities.filter((entity) => {
    return idFilter?.includes(entity.id) || nameFilter?.includes(entity.name)
  })
}

function isWithinBounds({ min, max }: QuantityTrigger, totalSelected: number): boolean {
  const withinMinBounds = min === undefined || totalSelected >= min
  const withinMaxBounds = max === undefined || totalSelected <= max
  return withinMinBounds && withinMaxBounds
}
